import { ActionIcon, Alert, Box, Card, Checkbox, Group, NumberInput, Radio, Select, Stack, Switch, TagsInput, Textarea, TextInput, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconInfoCircle, IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import type { ComponentInput } from "../../apis/chargeContract";
import { endCustomerCount } from "../../apis/chargeContract";
import { ComponentTypeIcon, componentTypeLabel } from "./ComponentTypeIcon";
import { EndCustomerRollupPreview } from "./EndCustomerRollupPreview";

const ORDER_TYPE_OPTIONS = [
  { value: "B2B_ORDER", label: "B2B захиалга" },
  { value: "AGGREGATOR_ORDER", label: "Агрегатор захиалга" },
  { value: "INTERNAL_TRADING", label: "Дотоод арилжаа" },
  { value: "RESELLER_ORDER", label: "Дилерийн захиалга" },
];

const CUSTOMER_TYPE_OPTIONS = [
  { value: "BUYER", label: "Худалдан авагч" },
  { value: "SUPPLIER", label: "Нийлүүлэгч" },
];

interface Props {
  value: ComponentInput;
  onChange: (next: ComponentInput) => void;
  onRemove?: () => void;
  currencyOptions: string[];
  // Tenant whose end-customers the END_CUSTOMER_RECURRING rollup preview counts.
  // Absent during the create flow before a tenant is chosen — then the count stays 0.
  tenantId?: string;
}

// txFeeRate is stored as a decimal (0.01 = 1%). The input shows it as a percent.
const rateToPercent = (rate?: string): number | undefined =>
  rate == null || rate === "" ? undefined : Number((Number(rate) * 100).toFixed(4));
const percentToRate = (pct: number | string): string =>
  pct === "" || pct == null ? "" : String(Number(pct) / 100);

export function ComponentEditor({ value, onChange, onRemove, currencyOptions, tenantId }: Props) {
  const patch = (p: Partial<ComponentInput>) => onChange({ ...value, ...p });

  const isViaTenat = value.ecCollectionMode === "VIA_TENANT";

  // Live rollup count for END_CUSTOMER_RECURRING: how many of the tenant's active
  // end-customers match the chosen types, minus exemptions. Server is the source of
  // truth (same query the billing cron uses), so the preview matches real billing.
  const isEcRecurring = value.componentType === "END_CUSTOMER_RECURRING";
  const typesKey = (value.ecApplicableCustomerTypes ?? []).join(",");
  const exemptedKey = (value.ecExemptedCustomerIds ?? []).join(",");
  const [customerCount, setCustomerCount] = useState(0);

  useEffect(() => {
    if (!isEcRecurring || !tenantId) {
      setCustomerCount(0);
      return;
    }
    // Debounce ~400ms so editing the types/exemptions doesn't fire a request per keystroke.
    const handle = setTimeout(() => {
      endCustomerCount(tenantId, {
        types   : typesKey ? typesKey.split(",") : [],
        exempted: exemptedKey ? exemptedKey.split(",") : []
      })
        .then((r) => setCustomerCount(r?.count ?? 0))
        .catch(() => setCustomerCount(0)); // fail quietly — never crash the editor
    }, 400);
    return () => clearTimeout(handle);
  }, [isEcRecurring, tenantId, typesKey, exemptedKey]);

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <ComponentTypeIcon type={value.componentType} size={20} />
            <Box fw={600}>{componentTypeLabel(value.componentType)}</Box>
          </Group>
          {onRemove && (
            <ActionIcon variant="subtle" color="red" onClick={onRemove} aria-label="remove">
              <IconTrash size={18} />
            </ActionIcon>
          )}
        </Group>

        <TextInput
          label="Нэр"
          required
          placeholder="Жишээ: Сарын платформ төлбөр"
          value={value.label ?? ""}
          onChange={(e) => patch({ label: e.currentTarget.value })}
        />

        {/* ── RECURRING_SUBSCRIPTION ──────────────────────────────────── */}
        {value.componentType === "RECURRING_SUBSCRIPTION" && (
          <>
            <TextInput
              label="Тайлбар"
              placeholder="Нэмэлт тайлбар (заавал биш)"
              value={value.description ?? ""}
              onChange={(e) => patch({ description: e.currentTarget.value })}
            />
            <NumberInput
              label="Дүн"
              required
              value={value.recurringAmount ? Number(value.recurringAmount) : undefined}
              onChange={(v) => patch({ recurringAmount: String(v ?? "") })}
              min={0}
              thousandSeparator=","
              hideControls
              suffix=" MNT"
            />
            <Select
              label="Давтамж"
              required
              data={[
                { value: "MONTHLY", label: "Сарын" },
                { value: "QUARTERLY", label: "Улирлын" },
                { value: "ANNUAL", label: "Жилийн" }
              ]}
              value={value.recurringFrequency ?? null}
              onChange={(v) => v && patch({ recurringFrequency: v as ComponentInput["recurringFrequency"] })}
            />
            <NumberInput
              label="Анкорын өдөр (1–31)"
              required
              min={1}
              max={31}
              value={value.recurringAnchorDay}
              onChange={(v) => patch({ recurringAnchorDay: Number(v) })}
            />
            <DateInput
              label="Эхлэх огноо"
              placeholder="Огноо сонгох"
              clearable
              valueFormat="YYYY-MM-DD"
              value={value.recurringStartsAt ?? null}
              onChange={(v) => patch({ recurringStartsAt: v || null })}
            />
            <DateInput
              label="Дуусах огноо"
              placeholder="Хязгааргүй бол хоосон"
              clearable
              valueFormat="YYYY-MM-DD"
              value={value.recurringEndsAt ?? null}
              onChange={(v) => patch({ recurringEndsAt: v || null })}
            />
            <Select
              label="Валют"
              data={currencyOptions.map((c) => ({ value: c, label: c }))}
              value={value.currency ?? null}
              onChange={(v) => v && patch({ currency: v })}
              w={200}
            />
            <Switch
              label="Идэвхтэй"
              checked={value.isEnabled ?? true}
              onChange={(e) => patch({ isEnabled: e.currentTarget.checked })}
            />
          </>
        )}

        {/* ── ONE_TIME_PURCHASE ──────────────────────────────────────── */}
        {value.componentType === "ONE_TIME_PURCHASE" && (
          <>
            <TextInput
              label="Тайлбар"
              placeholder="Нэмэлт тайлбар (заавал биш)"
              value={value.description ?? ""}
              onChange={(e) => patch({ description: e.currentTarget.value })}
            />
            <NumberInput
              label="Дүн"
              required
              value={value.oneTimeAmount ? Number(value.oneTimeAmount) : undefined}
              onChange={(v) => patch({ oneTimeAmount: String(v ?? "") })}
              min={0}
              thousandSeparator=","
              hideControls
              suffix=" MNT"
            />
            <Select
              label="Валют"
              data={currencyOptions.map((c) => ({ value: c, label: c }))}
              value={value.currency ?? null}
              onChange={(v) => v && patch({ currency: v })}
              w={200}
            />
            <DateInput
              label="Төлбөр эхлэх огноо"
              required
              placeholder="Огноо сонгох"
              clearable
              valueFormat="YYYY-MM-DD"
              value={value.oneTimeChargeableAfter ?? null}
              onChange={(v) => patch({ oneTimeChargeableAfter: v || null })}
            />
            <DateInput
              label="Лиценз дуусах огноо"
              placeholder="Хязгааргүй бол хоосон"
              clearable
              valueFormat="YYYY-MM-DD"
              value={value.oneTimeLicenseExpiresAt ?? null}
              onChange={(v) => patch({ oneTimeLicenseExpiresAt: v || null })}
            />
            <Switch
              label="Идэвхтэй"
              checked={value.isEnabled ?? true}
              onChange={(e) => patch({ isEnabled: e.currentTarget.checked })}
            />
          </>
        )}

        {/* ── TRANSACTION_FEE ───────────────────────────────────────── */}
        {value.componentType === "TRANSACTION_FEE" && (
          <>
            <NumberInput
              label="Хувь (%)"
              description="Хязгаар: 0-100% (платформын шийдвэр). Жишээ: 1.00 = 1%"
              min={0}
              max={100}
              step={0.01}
              decimalScale={2}
              suffix="%"
              value={rateToPercent(value.txFeeRate)}
              onChange={(v) => patch({ txFeeRate: percentToRate(v as number | string) })}
            />
            <NumberInput
              label="Хамгийн бага шимтгэл"
              value={value.txFeeMinAmount ? Number(value.txFeeMinAmount) : undefined}
              onChange={(v) => patch({ txFeeMinAmount: String(v ?? "") })}
              min={0}
              thousandSeparator=","
              hideControls
            />
            <NumberInput
              label="Хамгийн их шимтгэл"
              value={value.txFeeMaxAmount ? Number(value.txFeeMaxAmount) : undefined}
              onChange={(v) => patch({ txFeeMaxAmount: String(v ?? "") })}
              min={0}
              thousandSeparator=","
              hideControls
            />
            <Select
              label="Валют"
              data={currencyOptions.map((c) => ({ value: c, label: c }))}
              value={value.currency ?? null}
              onChange={(v) => v && patch({ currency: v })}
              w={200}
            />
            <Checkbox.Group
              label="Хамаарах захиалгын төрөл"
              description="Хоосон бол бүх төрөлд хамаарна"
              value={value.txFeeApplicableOrderTypes ?? []}
              onChange={(v) => patch({ txFeeApplicableOrderTypes: v })}
            >
              <Group gap="md" mt="xs">
                {ORDER_TYPE_OPTIONS.map((o) => (
                  <Checkbox key={o.value} value={o.value} label={o.label} />
                ))}
              </Group>
            </Checkbox.Group>
            <Switch
              label="Идэвхтэй"
              checked={value.isEnabled ?? true}
              onChange={(e) => patch({ isEnabled: e.currentTarget.checked })}
            />
          </>
        )}

        {/* ── FREE_OR_CUSTOM ─────────────────────────────────────────── */}
        {value.componentType === "FREE_OR_CUSTOM" && (
          <>
            <Textarea
              label="Нөхцөл"
              required
              value={value.customTerms ?? ""}
              onChange={(e) => patch({ customTerms: e.currentTarget.value })}
              minRows={3}
            />
            <DateInput
              label="Дуусах огноо"
              placeholder="Огноо сонгох"
              clearable
              valueFormat="YYYY-MM-DD"
              value={value.customExpiresAt ?? null}
              onChange={(v) => patch({ customExpiresAt: v || null })}
            />
            <Switch
              label="Идэвхтэй"
              checked={value.isEnabled ?? true}
              onChange={(e) => patch({ isEnabled: e.currentTarget.checked })}
            />
          </>
        )}

        {/* ── END_CUSTOMER_RECURRING ─────────────────────────────────── */}
        {value.componentType === "END_CUSTOMER_RECURRING" && (
          <>
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
              radius="sm"
            >
              <Text size="sm">
                Платформ нь тенантын харилцагч бүрт тогтмол төлбөр ногдуулна.
                Тенантын өөрийн TenantFeePolicy-аас тусдаа.
              </Text>
            </Alert>

            <NumberInput
              label="Харилцагч тус бүрийн дүн"
              required
              value={value.ecRecurringAmount ? Number(value.ecRecurringAmount) : undefined}
              onChange={(v) => patch({ ecRecurringAmount: String(v ?? "") })}
              min={0}
              thousandSeparator=","
              hideControls
              suffix=" MNT"
            />
            <Select
              label="Давтамж"
              required
              data={[
                { value: "MONTHLY", label: "Сарын" },
                { value: "QUARTERLY", label: "Улирлын" },
                { value: "ANNUAL", label: "Жилийн" }
              ]}
              value={value.ecRecurringFrequency ?? null}
              onChange={(v) => v && patch({ ecRecurringFrequency: v })}
            />
            <NumberInput
              label="Анкорын өдөр (1–31)"
              required
              min={1}
              max={31}
              value={value.ecRecurringAnchorDay ?? undefined}
              onChange={(v) => patch({ ecRecurringAnchorDay: Number(v) })}
            />

            <EndCustomerRollupPreview
              customerCount={customerCount}
              perCustomerAmount={value.ecRecurringAmount ? Number(value.ecRecurringAmount) : 0}
              currency={value.currency ?? "MNT"}
              frequency={(value.ecRecurringFrequency as "MONTHLY" | "QUARTERLY" | "ANNUAL") ?? "MONTHLY"}
            />

            <Radio.Group
              label="Цуглуулах горим"
              required
              value={value.ecCollectionMode ?? ""}
              onChange={(v) => patch({ ecCollectionMode: v, ecDelinquencyAction: v === "VIA_TENANT" ? null : value.ecDelinquencyAction })}
            >
              <Stack gap="xs" mt="xs">
                <Radio
                  value="DIRECT_FROM_CUSTOMER"
                  label="DIRECT_FROM_CUSTOMER — Платформ харилцагч бүрт нэхэмжлэл илгээнэ"
                />
                <Radio
                  value="VIA_TENANT"
                  label="VIA_TENANT — Тенант сарын нэхэмжлэлд нэгтгэн төлнө"
                />
                <Radio
                  value="DIRECT_WITH_TENANT_FALLBACK"
                  label="DIRECT_WITH_TENANT_FALLBACK — Эхлээд шууд; гацаа үед тенантаар"
                />
              </Stack>
            </Radio.Group>

            <Radio.Group
              label="Гацаа үеийн үйлдэл (зөвхөн DIRECT горим)"
              value={value.ecDelinquencyAction ?? ""}
              onChange={(v) => patch({ ecDelinquencyAction: v })}
              styles={{ label: { opacity: isViaTenat ? 0.4 : 1 } }}
            >
              <Stack gap="xs" mt="xs">
                <Radio
                  value="BLOCK_ORDERS"
                  label="BLOCK_ORDERS — Захиалга блоклох"
                  disabled={isViaTenat}
                />
                <Radio
                  value="WARN_ONLY"
                  label="WARN_ONLY — Зөвхөн анхааруулга"
                  disabled={isViaTenat}
                />
                <Radio
                  value="FALLBACK_TO_TENANT"
                  label="FALLBACK_TO_TENANT — Тенантад шилжүүлэх"
                  disabled={isViaTenat}
                />
              </Stack>
            </Radio.Group>

            <Checkbox.Group
              label="Хамаарах харилцагчийн төрөл"
              value={value.ecApplicableCustomerTypes ?? []}
              onChange={(v) => patch({ ecApplicableCustomerTypes: v })}
            >
              <Group gap="md" mt="xs">
                {CUSTOMER_TYPE_OPTIONS.map((o) => (
                  <Checkbox key={o.value} value={o.value} label={o.label} />
                ))}
              </Group>
            </Checkbox.Group>

            <TagsInput
              label="Чөлөөлөгдсөн харилцагчийн ID"
              description="ID оруулаад Enter дарна уу"
              placeholder="cust_abc123"
              value={value.ecExemptedCustomerIds ?? []}
              onChange={(v) => patch({ ecExemptedCustomerIds: v })}
            />

            <Select
              label="Валют"
              data={currencyOptions.map((c) => ({ value: c, label: c }))}
              value={value.currency ?? null}
              onChange={(v) => v && patch({ currency: v })}
              w={200}
            />

            <Switch
              label="Идэвхтэй"
              checked={value.isEnabled ?? true}
              onChange={(e) => patch({ isEnabled: e.currentTarget.checked })}
            />
          </>
        )}

        {/* Description shown globally only for types that don't have their own description field */}
        {value.componentType !== "RECURRING_SUBSCRIPTION" && value.componentType !== "ONE_TIME_PURCHASE" && value.componentType !== "END_CUSTOMER_RECURRING" && (
          <TextInput
            label="Тайлбар (заавал биш)"
            value={value.description ?? ""}
            onChange={(e) => patch({ description: e.currentTarget.value })}
          />
        )}
      </Stack>
    </Card>
  );
}
